import { action as createAction } from "@iva/store-utils";
import { ErrorPayload, isDone, ThunkResult } from "#store/types";
import { pilotService } from "#services";
import { Interpretation, MetadataTable } from "#services/pilotService/types";
import { IQueryTableResult } from "#services/pilotService/responses/types";
import { isTable } from "#/util/typeUtils";
import {
    tableFetchFailed,
    tableFetchStarted,
    tableFetchSuccess,
} from "#store/tables";
import {
    FetchInterpretationFailedActionType,
    FetchInterpretationListStartedActionType,
    FetchInterpretationStartedActionType,
    FetchInterpretationSucceededActionType,
    InterpretationIdentifiers,
} from "#store/interpretations/interpretationTypes";
import { shouldRefetch } from "#/util/shouldRefetch";

/*******************************************************************************
 * actions
 ******************************************************************************/
import { queryClient } from "#/index";
import { setInterpretationFav } from "#store/ui";

export function fetchInterpretationTableFromServer(
    interpretationId: number
): ThunkResult {
    return (dispatch, getState) => {
        const interpretationState =
            getState().interpretations.interpretations[interpretationId];
        if (isDone(interpretationState)) {
            const tableId = interpretationState.payload.table;
            if (tableId !== undefined) {
                if (shouldRefetch(getState().tables.tableResults[tableId])) {
                    dispatch(tableFetchStarted(tableId));

                    const promise: Promise<IQueryTableResult> =
                        queryClient.fetchQuery(
                            `interpretation-${interpretationId}`,
                            () => {
                                return pilotService
                                    .interpretationService(
                                        getState().environment.SERVICE_URL,
                                        getState().uiState.rid
                                    )
                                    .getTable(interpretationId)
                                    .then((table: any) => {
                                        if (isTable(table)) {
                                            const query =
                                                String(table.meta?.sql) ||
                                                "forgotten query";
                                            const t = new IQueryTableResult(
                                                table,
                                                tableId,
                                                interpretationId
                                            );
                                            dispatch(tableFetchSuccess(t));
                                            return Promise.resolve(t);
                                        } else {
                                            const error = new Error(
                                                "not a table"
                                            );
                                            dispatch(
                                                tableFetchFailed({
                                                    id: tableId,
                                                    error: error,
                                                    message: `failed to receive table for id ${interpretationId}`,
                                                })
                                            );
                                            return Promise.reject({
                                                id: tableId,
                                                error: error,
                                                message: `failed to parse response into a IQueryTableResult`,
                                            });
                                        }
                                    })
                                    .catch(error => {
                                        dispatch(
                                            tableFetchFailed({
                                                id: tableId,
                                                error: error,
                                                message: `failed to receive table for id ${interpretationId}`,
                                            })
                                        );
                                        return Promise.reject({
                                            id: tableId,
                                            error: error,
                                            message: `failed to receive table for id ${interpretationId}`,
                                        });
                                    });
                            }
                        );
                }
            }
        } else {
            // wait for pending interpretation fetch or refetch?
        }
    };
}

function fetchInterpretationStarted(
    id: number
): FetchInterpretationStartedActionType {
    return createAction(
        InterpretationIdentifiers.FETCH_FROM_SERVER_STARTED,
        id
    );
}

function fetchInterpretationFailed(
    error: ErrorPayload
): FetchInterpretationFailedActionType {
    return createAction(
        InterpretationIdentifiers.FETCH_FROM_SERVER_FAILED,
        error
    );
}

function fetchInterpretationSucceeded(
    r: Interpretation
): FetchInterpretationSucceededActionType {
    return createAction(
        InterpretationIdentifiers.FETCH_FROM_SERVER_SUCCEEDED,
        r
    );
}

function fetchInterpretationsListStarted(): FetchInterpretationListStartedActionType {
    return createAction(
        InterpretationIdentifiers.FETCH_LIST_STARTED,
        undefined
    );
}

function fetchInterpretationsListFailed(
    error: ErrorPayload
): FetchInterpretationFailedActionType {
    return createAction(
        InterpretationIdentifiers.FETCH_FROM_SERVER_FAILED,
        error
    );
}

function fetchInterpretationsListSucceeded() {
    return createAction(
        InterpretationIdentifiers.FETCH_LIST_SUCCEEDED,
        undefined
    );
}

export function fetchInterpretationFromServer(id: number): ThunkResult {
    return (dispatch, getState) => {
        if (shouldRefetch(getState().interpretations.interpretations[id])) {
            dispatch(fetchInterpretationStarted(id));

            pilotService
                .interpretationService(
                    getState().environment.SERVICE_URL,
                    getState().uiState.rid
                )
                .get(id)
                .then(result => {
                    dispatch(
                        createAction(
                            InterpretationIdentifiers.FETCH_FROM_SERVER_SUCCEEDED,
                            result
                        ) as FetchInterpretationSucceededActionType
                    );
                    dispatch(fetchInterpretationTableFromServer(id));
                })
                .catch(error => {
                    /** remove interpretation from favs if failed */
                    dispatch(setInterpretationFav(id, false));
                    dispatch(
                        fetchInterpretationFailed({
                            error,
                            id,
                            message: `failed to receive interpretation ${id}`,
                        })
                    );
                });
        }
    };
}

export function listInterpretationsFromServer(): ThunkResult {
    return (dispatch, getState) => {
        dispatch(fetchInterpretationsListStarted());

        pilotService
            .interpretationService(
                getState().environment.SERVICE_URL,
                getState().uiState.rid
            )
            .list()
            .then(interpretations => {
                dispatch(fetchInterpretationsListSucceeded());
                interpretations.forEach(r =>
                    dispatch(fetchInterpretationSucceeded(r))
                );
            })
            .catch(error => {
                dispatch(
                    fetchInterpretationsListFailed({
                        ...error,
                        message: "Unable to retrieve list of interpretations",
                    })
                );
            });
    };
}
